<template>
    <div id="reset-password">
        <div class="d-flex justify-content-center spinner-border text-primary spinner-position form" v-if="loading"></div>
        <div class="spinner" v-if="loading"></div>
        
        <div class="login-wrapper" :style="backgroundStyle">
            <div class="glass-login-card">
                <div class="logo-brand mb-4">
                    <img id="logo-brand" :src="corporateLogo" alt="logo" v-if="corporateLogo">
                </div>

                <!-- Password Changed -->
                <div v-if="status" class="text-center">
                    <h4 class="mb-4" style="color: #333333;">Password Changed</h4>
                    <p style="font-size: 14px; color: #6c757d;">Congratulations! Your password has been successfully changed. It's important to keep your password secure and confidential. If you have any concerns or issues, don't hesitate to reach out for assistance. Take care!</p>
                    <div class="d-flex align-items-center flex-column mt-5">    
                        <router-link :to="loginLink" class="text-link" style="text-decoration: underline;"><small>Back to login</small></router-link>
                    </div>
                </div>

                <!-- Reset your password -->
                <form @submit.prevent="resetPassword" v-if="!status && validationToken">
                    <h4 class="text-center mb-2" style="color: #333333;">Reset your password</h4>
                    <p class="text-center mb-4" style="font-size: 13px; color: #6c757d;">Please enter your new password to sign in to the membership system</p>

                    <div v-if="validation !== ''" class="alert alert-danger custom-alert mb-3">
                        <div class="alert-content">
                            <span class="alert-icon">&#9888;</span>
                            <p>{{ validation }}</p>
                        </div>
                    </div>

                    <div class="mb-3 text-start">
                        <label class="glass-label" style="font-size: 13px; color: #333333; margin-bottom: 5px; display: block;">Password</label>
                        <div class="position-relative">
                            <input name="password" v-model="new_data.password" :type="showPassword ? 'text' : 'password'" required placeholder="Password" class="glass-input" style="padding-right: 40px;" autocomplete="new-password"/>
                            <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'" @click="showPassword = !showPassword"
                                style="position: absolute; right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"></i>
                        </div>
                    </div>

                    <div class="mb-4 text-start">
                        <label class="glass-label" style="font-size: 13px; color: #333333; margin-bottom: 5px; display: block;">Confirm Password</label>
                        <div class="position-relative">
                            <input name="password_confirmation" v-model="new_data.password_confirmation" :type="showConfirmPassword ? 'text' : 'password'" required placeholder="Confirm password" class="glass-input" style="padding-right: 40px;" autocomplete="new-password"/>
                            <i class="fas" :class="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'" @click="showConfirmPassword = !showConfirmPassword"
                                style="position: absolute; right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"></i>
                        </div>
                    </div>

                    <div class="d-flex justify-content-center w-100 mb-4">
                        <button type="submit" class="login-button">
                            Reset password
                        </button>
                    </div>

                    <div class="d-flex justify-content-center mt-1">
                        <router-link :to="loginLink" class="text-link text-center" style="text-decoration: underline;">
                            <small>Back to login</small>
                        </router-link>
                    </div>
                </form>

                <!-- Expired Token -->
                <div v-if="!status && !validationToken && !resendEmail" class="text-center">
                    <h4 class="mb-4" style="color: #333333;">Expired Token</h4>
                    <p style="font-size: 14px; color: #6c757d;">Please request a new forgot password email</p>

                    <div v-if="validation !== ''" class="alert alert-danger custom-alert mb-3">
                        <div class="alert-content">
                            <span class="alert-icon">&#9888;</span>
                            <p>{{ validation }}</p>
                        </div>
                    </div>

                    <div class="d-flex justify-content-center w-100 mt-4">
                        <button type="button" @click="forgotPass" class="login-button">
                            Request password reset
                        </button>
                    </div>
                </div>

                <!-- Resend Email -->
                <div v-if="resendEmail" class="text-center">
                    <h4 class="mb-4" style="color: #333333;">We sent an email</h4>
                    <p style="font-size: 14px; color: #6c757d;">Please check your email and follow the instructions provided to reset your password. If you do not see the email in your inbox, please check your spam or junk folder.</p>
                </div>

            </div>
            
            <div class="login-footer">
                Copyright © PT. Omni Hotelier Internasional {{ currentYear }}
            </div>
        </div>
    </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import api from '@/helpers/api';

export default {
    name: 'ResetPassword',
    setup() {
        const store = useStore();
        const route = useRoute();

        const new_data = reactive({
            password: '',
            password_confirmation: '',
            token: route.params.token,
            email: route.query.email
        });

        const loading = ref(false);
        const validation = ref('');
        const validationToken = ref(false);
        const resendEmail = ref(false);
        const status = ref(false);
        
        const showPassword = ref(false);
        const showConfirmPassword = ref(false);

        const loginBackground = ref('');
        const corporateLogo = ref('');
        const currentYear = new Date().getFullYear();
        const isNewAuthActive = ref(false);

        const loginLink = computed(() => {
            const merchantId = localStorage.getItem('merchant_id');
            const query = merchantId ? { merchant_id: merchantId } : {};
            
            if (!isNewAuthActive.value) {
                return { name: 'Login', query };
            }

            // Determine origin from localStorage (set by MemberLogin/BackofficeLogin on forgot password click)
            const authOrigin = localStorage.getItem('auth_origin');
            
            if (authOrigin === 'backoffice') {
                return { name: 'BackofficeLogin', query };
            } else if (authOrigin === 'member') {
                return { name: 'MemberLogin', query };
            }

            // Fallback: check browser history state
            const backUrl = window.history.state?.back || '';
            
            if (backUrl.includes('/backoffice/login')) {
                return { name: 'BackofficeLogin', query };
            } else if (backUrl.includes('/member/login')) {
                return { name: 'MemberLogin', query };
            }
            
            return { name: 'Login', query };
        });

        const backgroundStyle = computed(() => ({
            backgroundImage: loginBackground.value ? `url(${loginBackground.value})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        }));

        const checkImage = (url) => {
            return new Promise((resolve) => {
                const img = new window.Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = url;
            });
        };

        onMounted(() => {
            loading.value = true;

            store.dispatch('Auth/checkTokenPasswordReset', new_data)
            .then(result => {
                if (result.status) {
                    validationToken.value = result.status;
                }
            }).catch(() => {
                validationToken.value = false;
            }).finally(() => {
                loading.value = false;
            });

            // Fetch UI Config
            loginBackground.value = localStorage.getItem('loginBackground') || '/img/temporary-login.png';
            corporateLogo.value = localStorage.getItem('corporateLogo') || '/img/icons/Color_Alternate-1024x381.png';

            api.request('get', `/api/public/corporate-id`)
                .then(async (response) => {
                    const corporateId = response.data.data;
                    const s3Url = process.env.VUE_APP_S3_BUCKET;
                    
                    const bgUrl = `${s3Url}user_assets/${corporateId}/loginBackground.png`;
                    const bgExists = await checkImage(bgUrl);
                    if (bgExists) {
                        loginBackground.value = bgUrl;
                    }

                    const extensions = ['png', 'jpg', 'jpeg', 'webp'];
                    for (const ext of extensions) {
                        const logoUrl = `${s3Url}user_assets/${corporateId}/corporateLogo.${ext}`;
                        if (await checkImage(logoUrl)) {
                            corporateLogo.value = logoUrl;
                            break;
                        }
                    }
                })
                .catch(error => {
                    console.error('Failed request', error);
                });

            // Check new_auth flag status
            api.request('get', '/api/public/auth-module-status')
                .then((response) => {
                    isNewAuthActive.value = response.data?.data?.enabled === true;
                })
                .catch(() => {
                    isNewAuthActive.value = false;
                });
        });

        const resetPassword = () => {
            loading.value = true;
            validation.value = '';

            store.dispatch('Auth/sendPasswordReset', new_data)
            .then(result => {
                if (result.status) {
                    status.value = result.status;
                }
            }).catch(response => {
                const error_msg = response?.data?.error || response?.data?.message || 'Failed to reset password';
                validation.value = error_msg;
            }).finally(() => {
                loading.value = false;
            });
        }

        const forgotPass = () => {
            loading.value = true;
            validation.value = '';

            store.dispatch('Auth/sendResetRequest', new_data.email)   
            .then(() => {
                status.value = false;
                validationToken.value = false;
                resendEmail.value = true;
            }).catch(response => {
                const error_msg = response?.data?.error || response?.data?.message || 'Failed to send reset link';
                validation.value = error_msg;
            }).finally(() => {
                status.value = false;
                loading.value = false;
            });
        }

        return {
            new_data,
            loading,
            validation,
            status,
            resetPassword,
            validationToken,
            forgotPass,
            resendEmail,
            showPassword,
            showConfirmPassword,
            loginLink,
            backgroundStyle,
            corporateLogo,
            currentYear
        }
    },
}
</script>

<style scoped>
/* --- New Glassmorphism Styles --- */
.login-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100vh;
    width: 100%;
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 20px 16px;
}
.login-wrapper::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
}
.glass-login-card {
    background: #ffffff;
    border-radius: 8px;
    padding: 2.5rem;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
    z-index: 1;
    display: flex;
    flex-direction: column;
    margin: auto 0;
}
.glass-input {
    width: 100%;
    background: #f8f9fa;
    border: 1px solid #ced4da;
    color: #333333;
    padding: 12px;
    border-radius: 8px;
    outline: none;
    transition: all 0.3s ease;
}
.glass-input::placeholder {
    color: #6c757d;
}
.glass-input:focus {
    background: #ffffff;
    border-color: #DE5B53;
}
.custom-input-group {
    position: relative;
}
.input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 14px;
}
.text-link {
    color: #DE5B53;
    text-decoration: none;
    transition: color 0.3s ease;
}
.text-link:hover {
    color: #c44e47;
}
.login-button {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    background-color: #DE5B53;
    color: white;
    border: none;
    font-weight: 500;
    transition: background-color 0.3s ease;
}
.login-button:hover {
    background-color: #c44e47;
    transform: none;
}
.login-button:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}
.login-footer {
    position: relative;
    margin-top: 20px;
    width: 100%;
    text-align: center;
    color: white;
    z-index: 0;
    font-size: 13px;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.8);
}
.logo-brand {
    justify-content: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}
#logo-brand {
    display: grid;
    width: 85%;
    padding-bottom: -10%;
}
.custom-alert {
    padding: 10px 15px;
    font-size: 13px;
    border-radius: 8px;
    display: flex;
    align-items: center;
}
.alert-content {
    display: flex;
    align-items: center;
    gap: 8px;
}
.alert-content p {
    margin: 0;
}

input:-webkit-autofill,
input:-webkit-autofill:hover, 
input:-webkit-autofill:focus, 
input:-webkit-autofill:active {
  transition: background-color 5000s ease-in-out 0s, box-shadow 5000s ease-in-out 0s !important;
  -webkit-text-fill-color: #333333 !important;
}
input:disabled {
  background-color: #e9ecef !important;
  color: #6c757d !important;
  -webkit-text-fill-color: #6c757d !important;
}

@media (max-width: 375px) {
  .row-wrapper { flex-direction: column; }
}
</style>